// src/components/layout/Sidebar.js
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  TeamOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  LogoutOutlined,
  BellOutlined,
  UserSwitchOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CarryOutOutlined, 
} from "@ant-design/icons";
import { HiChevronDoubleLeft } from "react-icons/hi";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Logo from "../../assets/logo.png";
import useAuth from "../../hooks/useAuth";
import { getRolByNombre } from "../../services/rolesService";

/** Catálogo UI (no permisos) */
const NAV_CATALOG = {
  dashboard: {
    label: "Dashboard",
    icon: <DashboardOutlined className="text-lg" />,
    to: "/futbolito",
    isActive: (path) =>
      path === "/futbolito" || path.startsWith("/futbolito/dashboard"),
  },
  colaboradores: {
    label: "Colaboradores",
    icon: <UsergroupAddOutlined className="text-lg" />,
    to: "/futbolito/colaboradores",
    isActive: (path) => path.startsWith("/futbolito/colaboradores"),
  },
  representantes: {
    label: "Representantes",
    icon: <UserOutlined className="text-lg" />,
    to: "/futbolito/representantes",
    isActive: (path) => path.startsWith("/futbolito/representantes"),
  },
  deportistas: {
    label: "Deportistas",
    icon: <TeamOutlined className="text-lg" />,
    to: "/futbolito/deportistas",
    isActive: (path) => path.startsWith("/futbolito/deportistas"),
  },
  grupos: {
    label: "Grupos",
    icon: <AppstoreOutlined className="text-lg" />,
    to: "/futbolito/grupos",
    isActive: (path) => path.startsWith("/futbolito/grupos"),
  },
  eventos: {
    label: "Eventos",
    icon: <CalendarOutlined className="text-lg" />,
    to: "/futbolito/eventos",
    isActive: (path) => path.startsWith("/futbolito/eventos"),
  },
  asistencias: {
    label: "Asistencias",
    icon: <CarryOutOutlined className="text-lg" />,
    to: "/futbolito/asistencias",
    isActive: (path) => path.startsWith("/futbolito/asistencias"),
  },
  notificaciones: {
    label: "Notificaciones",
    icon: <BellOutlined className="text-lg" />,
    to: "/futbolito/notificaciones",
    isActive: (path) => path.startsWith("/futbolito/notificaciones"),
  },
  roles: {
    label: "Roles",
    icon: <UserSwitchOutlined className="text-lg" />,
    to: "/futbolito/roles",
    isActive: (path) => path.startsWith("/futbolito/roles"),
  },
  pagos: {
    label: "Pagos",
    icon: <DollarOutlined className="text-lg"/>,
    to: "/futbolito/pagos",
    isActive: (path) => path.startsWith("/futbolito/pagos"),
  },
  productos: {
    label: "Productos",
    icon: <ShoppingCartOutlined className="text-lg" />,
    to: "/futbolito/productos",
    isActive: (path) => path.startsWith("/futbolito/productos"),
  },
};

/** Normaliza permisos API a booleanos de lectura (SOLO read: true) */
function normalizeReadPerms(permisosRaw) {
  const out = {};
  if (!permisosRaw || typeof permisosRaw !== "object") return out;

  for (const key of Object.keys(permisosRaw)) {
    const v = permisosRaw[key];

    // Si es un objeto con propiedad 'read'
    if (v && typeof v === "object" && "read" in v) {
      out[key] = Boolean(v.read);
    }
    // Si es directamente un booleano (por compatibilidad)
    else if (typeof v === "boolean") {
      out[key] = v;
    }
    // Cualquier otro caso = false (no tiene permiso de lectura)
    else {
      out[key] = false;
    }
  }
  return out;
}

export default function Sidebar({
  isCollapsed = false,
  isMobile = false,
  isOpen = true,
  onClose,
  onToggleCollapse,
  onOpenMobile, // ⬅️ NUEVO: para abrir en móvil con el botón flotante
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const cancelBtnRef = useRef(null);

  const [readPerms, setReadPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [permError, setPermError] = useState(null);
  const lastLoadedRole = useRef("");
  const hasRedirected = useRef(false); // Prevenir redirecciones múltiples

  // Cerrar modal con ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setShowLogoutModal(false);
    }
    if (showLogoutModal) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLogoutModal]);

  // Focus al abrir modal
  useEffect(() => {
    if (showLogoutModal && cancelBtnRef.current) {
      cancelBtnRef.current.focus();
    }
  }, [showLogoutModal]);

  // Datos de usuario
  const nombres = user?.user_metadata?.nombres || "";
  const apellidos = user?.user_metadata?.apellidos || "";
  const displayName =
    nombres || apellidos
      ? `${nombres} ${apellidos}`.trim()
      : user?.email || "Usuario";
  const rolRaw = user?.user_metadata?.rol || user?.role || "";
  const rolNorm = (rolRaw ?? "").toString(); // sin normalizar, tal cual
  const email = user?.email || "—";

  /** Carga de permisos desde API (solo read:true) */
  useEffect(() => {
    let disposed = false;

    async function load() {
      const role = rolNorm;
      if (!role) {
        setReadPerms({});
        return;
      }

      // Evitar recarga si ya tenemos los permisos de este rol
      if (lastLoadedRole.current === role && readPerms !== null) return;

      setLoadingPerms(true);
      setPermError(null);

      try {
        const data = await getRolByNombre(role);
        if (!disposed) {
          const normalized = normalizeReadPerms(data?.permisos);
          setReadPerms(normalized);
          lastLoadedRole.current = role;
          hasRedirected.current = false; // Resetear flag de redirección
        }
      } catch (e) {
        if (!disposed) {
          setPermError(e?.message || "No se pudieron obtener permisos");
          setReadPerms({});
        }
      } finally {
        if (!disposed) setLoadingPerms(false);
      }
    }

    load();

    return () => {
      disposed = true;
    };
  }, [rolNorm]); // Solo depende del rol

  /** Construye items visibles SOLO con read:true */
  const items = useMemo(() => {
    if (!readPerms) return [];

    // Filtrar solo los módulos con read: true
    const allowedModules = Object.keys(readPerms).filter(
      (module) => readPerms[module] === true
    );

    // Mantener el orden del catálogo
    const orderedKeys = Object.keys(NAV_CATALOG).filter((key) =>
      allowedModules.includes(key)
    );

    return orderedKeys.map((key) => {
      const def = NAV_CATALOG[key];
      return {
        module: key,
        to: def.to,
        label: def.label,
        icon: def.icon,
        active: def.isActive(location.pathname),
      };
    });
  }, [readPerms, location.pathname]);

  /** Redirección ÚNICA al cargar permisos (no en cada click) */
  useEffect(() => {
    // Solo ejecutar si ya cargamos permisos y no hemos redirigido
    if (!readPerms || hasRedirected.current || loadingPerms) return;

    // Si no hay items permitidos, no hacer nada
    if (items.length === 0) return;

    // Verificar si la ruta actual está permitida
    const currentModuleEntry = Object.entries(NAV_CATALOG).find(([_, def]) =>
      def.isActive(location.pathname)
    );

    if (!currentModuleEntry) {
      // No estamos en ninguna ruta del catálogo, no redirigir
      return;
    }

    const [currentModule] = currentModuleEntry;
    const isCurrentAllowed = readPerms[currentModule] === true;

    // Solo redirigir si la ruta actual NO está permitida
    if (!isCurrentAllowed) {
      const firstAllowedRoute = items[0]?.to;
      if (firstAllowedRoute) {
        hasRedirected.current = true; // Marcar que ya redirigimos
        navigate(firstAllowedRoute, { replace: true });
      }
    }
  }, [readPerms, items, location.pathname, navigate, loadingPerms]);

  const widthClass = isMobile
    ? isOpen
      ? "w-64"
      : "-translate-x-full"
    : isCollapsed
    ? "w-16"
    : "w-64";

  const handleItemClick = () => {
    if (isMobile && onClose) onClose();
  };

  const askLogout = () => setShowLogoutModal(true);

  const confirmLogout = () => {
    try {
      if (logout) logout("manual");
      else navigate("/");
    } catch {
      navigate("/");
    } finally {
      setShowLogoutModal(false);
      if (isMobile && onClose) onClose();
    }
  };

  /** Handler del botón flotante en móvil */
  const handleMobileFabClick = () => {
    if (!isMobile) return;

    if (isOpen) {
      // Si está abierto, lo cerramos
      if (onClose) onClose();
    } else {
      // Si está cerrado, pedimos al padre que lo abra
      if (onOpenMobile) onOpenMobile();
    }
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen bg-white shadow-xl transition-all duration-300 z-50
          ${widthClass}
          ${isMobile ? "lg:hidden" : ""}
        `}
      >
        <div className="flex flex-col h-full text-black">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <img src={Logo} alt="Logo" className="h-12 w-auto" />
              {!isCollapsed && !isMobile && (
                <span className="font-semibold text-sm text-[#012047]">
                  Smart FC Atahualpa
                </span>
              )}
            </div>

            {isMobile ? (
              <button
                onClick={onClose}
                className="text-[#012047] bg-gray-200 hover:bg-gray-300 p-2 rounded-md"
                aria-label="Cerrar menú"
              >
                <HiChevronDoubleLeft className="text-lg" />
              </button>
            ) : (
              <button
                onClick={onToggleCollapse}
                className="text-[#012047] bg-gray-200 hover:bg-gray-300 p-2 rounded-md"
                aria-label="Colapsar menú"
                title={isCollapsed ? "Expandir" : "Colapsar"}
              >
                <HiChevronDoubleLeft
                  className={`text-lg transition-transform duration-200 ${
                    isCollapsed ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
          </div>

          {/* Usuario */}
          <div className="px-4 py-3 border-b border-gray-200">
            {!isCollapsed || isMobile ? (
              <div className="text-xs">
                <div className="font-medium text-[#012047] truncate">
                  {displayName}
                </div>
                <div className="text-gray-600 truncate">{email}</div>
                <span className="inline-flex items-center mt-1 rounded-full bg-[#e7eefc] text-[#012047] px-2 py-0.5 text-[11px]">
                  {rolRaw}
                </span>
                {loadingPerms && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    Cargando permisos…
                  </div>
                )}
                {permError && (
                  <div className="mt-1 text-[11px] text-rose-600">
                    {permError}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="w-8 h-8 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={Logo}
                    alt="Logo"
                    className="w-8 h-8 object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto">
            <ul className="p-2 space-y-1">
              {items.map((it) => (
                <li key={it.to}>
                  <Link
                    to={it.to}
                    onClick={handleItemClick}
                    className={`group flex items-center p-3 rounded-lg transition ${
                      it.active
                        ? "bg-[#e7eefc] text-[#012047]"
                        : "hover:bg-gray-100"
                    }`}
                    title={isCollapsed ? it.label : undefined}
                  >
                    {it.icon}
                    {(!isCollapsed || isMobile) && (
                      <span className="ml-3 text-sm">{it.label}</span>
                    )}
                  </Link>
                </li>
              ))}

              {!loadingPerms && items.length === 0 && (
                <li className="px-3 py-2 text-xs text-gray-500">
                  No tienes módulos disponibles para tu rol.
                </li>
              )}
            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={askLogout}
              className="flex items-center w-full p-2 rounded-lg hover:bg-gray-100 transition text-rose-600"
              title={isCollapsed ? "Cerrar sesión" : undefined}
            >
              <LogoutOutlined className="text-lg" />
              {(!isCollapsed || isMobile) && (
                <span className="ml-3 text-sm">Cerrar sesión</span>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay mobile: click fuera = cerrar */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Botón flotante mobile */}
      {isMobile && (
        <button
          type="button"
          onClick={handleMobileFabClick}
          className="
            fixed bottom-4 right-4 lg:hidden
            w-12 h-12 rounded-full shadow-xl
            bg-[#012047] text-white
            flex items-center justify-center
            z-[55]
          "
          aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {isOpen ? (
            <HiChevronDoubleLeft className="text-xl" />
          ) : (
            <MenuOutlined className="text-xl" />
          )}
        </button>
      )}

      {/* Modal Confirmar Logout */}
      {showLogoutModal && (
        <div
          aria-modal="true"
          role="dialog"
          aria-labelledby="logout-title"
          aria-describedby="logout-desc"
          className="fixed inset-0 z-[60] flex items-center justify-center"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowLogoutModal(false)}
          />
          <div className="relative z-[61] w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-gray-200 p-5">
            <h3
              id="logout-title"
              className="text-base font-semibold text-[#012047]"
            >
              ¿Cerrar sesión?
            </h3>
            <p id="logout-desc" className="mt-1 text-sm text-gray-600">
              Se cerrará tu sesión actual y volverás a la pantalla de inicio.
            </p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                ref={cancelBtnRef}
                onClick={() => setShowLogoutModal(false)}
                className="inline-flex items-center rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={confirmLogout}
                className="inline-flex items-center rounded-xl bg-rose-600 px-3 py-2 text-sm text-white hover:bg-rose-700"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
